import { datadogLogs } from "@datadog/browser-logs";

import { DATADOG_SERVICE, DATADOG_SITE, datadogEnv, datadogVersion } from "./datadog";

let initialised = false;

/**
 * Initialise Datadog Browser Logs — ships frontend log lines straight from the
 * browser to Datadog's Logs product (alongside the nginx/backend logs).
 *
 * Reuses the same client token as RUM (`VITE_DATADOG_RUM_CLIENT_TOKEN`): Browser
 * Logs needs only a client token, no application id. No-ops when the token is
 * absent (local dev / un-provisioned build), so nothing is sent and
 * `createLogger()` falls back to the browser console.
 *
 * Deliberately low-noise: automatic capture is OFF — RUM (ADR 0028) already
 * records uncaught exceptions and failed requests, so mirroring them here would
 * double-record the same event — and `console.*` is not forwarded. Only explicit
 * `createLogger(...).error()` calls reach Datadog. Call once in main.tsx.
 */
export function initDatadogLogs(): void {
  const clientToken = import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN;
  if (!clientToken) {
    return;
  }

  datadogLogs.init({
    clientToken,
    site: DATADOG_SITE,
    service: DATADOG_SERVICE,
    env: datadogEnv(),
    version: datadogVersion(),
    forwardErrorsToLogs: false,
    forwardConsoleLogs: [],
    sessionSampleRate: 100,
  });
  initialised = true;
}

export interface WebLogger {
  /**
   * Log an error. Sent to Datadog Logs when Browser Logs is initialised,
   * otherwise written to the browser console (local dev).
   */
  error: (message: string, context?: Record<string, unknown>, error?: Error) => void;
}

/**
 * Create a module-scoped error logger. Pass the module's path so every line is
 * searchable by where it came from:
 *
 *   const log = createLogger("views/AgentProfileView.tsx");
 *   log.error("failed to load agent", { agentId }, err);
 *
 * Errors only, by design — keeps Datadog Logs low-noise. The path is attached as
 * `logSource`, NOT Datadog's reserved `source` attribute (which selects the log
 * pipeline — the same reason the web task definition sets `dd_source: nginx`
 * rather than overloading it).
 */
export function createLogger(pathToFile: string): WebLogger {
  return {
    error(message, context, error) {
      if (initialised) {
        datadogLogs.logger.error(message, { logSource: pathToFile, ...context }, error);
        return;
      }
      // Local dev / un-provisioned build: no client token, so surface the error
      // in the browser console for the developer instead.
      console.error(`[ERROR] [${pathToFile}] - ${message}`, error ?? "", context ?? "");
    },
  };
}
